import React, { useEffect, useMemo, useState } from "react";
import { runGraphQL } from "../nr1/nerdgraph.js";
import { currentMarket } from "./market";

/**
 * Every host in the store, grouped by device type.
 *
 * The service dashboards show a store's hosts under Related Entities, but only
 * the two or three that service touches; a store entity has no relationships at
 * all, so its rail was empty. These come from the hosts' own tags instead —
 * `store.number` is what ties a host to a store, and `device.type` is the
 * grouping the estate is actually organised by (BOS, CSO, EPR, GSC, KVS, ORB,
 * POS, RHS). Opening one pushes the standard host entity view, the same as
 * clicking a related entity does.
 */

const HOST_SEARCH = `
  query StoreHosts($query: String!, $cursor: String) {
    actor {
      entitySearch(query: $query) {
        count
        results(cursor: $cursor) {
          nextCursor
          entities {
            guid
            name
            alertSeverity
            reporting
            tags { key values }
          }
        }
      }
    }
  }
`;

const tag = (entity, key) =>
  (entity.tags || []).find((t) => t.key === key)?.values?.[0] || null;

/** Worst severity in a group, so a collapsed group still shows trouble. */
const RANK = { CRITICAL: 3, WARNING: 2, NOT_ALERTING: 1 };
const worst = (hosts) =>
  hosts.reduce(
    (acc, h) => ((RANK[h.severity] || 0) > (RANK[acc] || 0) ? h.severity : acc),
    "NOT_ALERTING"
  );

export default function StoreHosts({ storeId, onOpenEntity }) {
  const market = useMemo(currentMarket, []);
  const [state, setState] = useState({ loading: true, hosts: [], error: null });
  const [open, setOpen] = useState({});

  useEffect(() => {
    if (!market || !storeId) {
      setState({ loading: false, hosts: [], error: null });
      return undefined;
    }

    let cancelled = false;
    const controller = new AbortController();
    setState({ loading: true, hosts: [], error: null });

    // Paginate: a large store can exceed the 25 entities a page returns, and
    // silently showing the first 25 of 34 would be a lie about the estate.
    (async () => {
      const query = `accountId = ${market.accountId} AND domain = 'INFRA' AND tags.store.number = '${storeId}'`;
      const found = [];
      let cursor = null;
      do {
        const data = await runGraphQL({
          query: HOST_SEARCH,
          variables: { query, cursor },
          signal: controller.signal,
        });
        const results = data?.actor?.entitySearch?.results;
        for (const entity of results?.entities || []) {
          found.push({
            guid: entity.guid,
            name: entity.name,
            severity: entity.alertSeverity || "NOT_ALERTING",
            reporting: entity.reporting !== false,
            deviceType: tag(entity, "device.type") || "Other",
          });
        }
        cursor = results?.nextCursor || null;
      } while (cursor);

      if (!cancelled) {
        found.sort((a, b) => a.name.localeCompare(b.name));
        setState({ loading: false, hosts: found, error: null });
      }
    })().catch((error) => {
      if (!cancelled && error?.name !== "AbortError") {
        setState({ loading: false, hosts: [], error });
      }
    });

    return () => {
      cancelled = true;
      controller.abort();
    };
  }, [market, storeId]);

  const { loading, hosts, error } = state;

  const groups = useMemo(() => {
    const byType = new Map();
    for (const host of hosts) {
      if (!byType.has(host.deviceType)) byType.set(host.deviceType, []);
      byType.get(host.deviceType).push(host);
    }
    return [...byType.entries()]
      .map(([type, list]) => ({ type, hosts: list, severity: worst(list) }))
      .sort((a, b) => a.type.localeCompare(b.type));
  }, [hosts]);

  return (
    <section className="entity-section">
      <h3>
        Store hosts{hosts.length ? ` (${hosts.length})` : ""}
      </h3>

      {loading ? <p className="entity-panel__muted">Loading hosts…</p> : null}

      {error ? (
        <p className="entity-panel__muted" role="alert">
          Hosts unavailable: {error.message}
        </p>
      ) : null}

      {!loading && !error && !market ? (
        <p className="entity-panel__muted">No market in view to search.</p>
      ) : null}

      {!loading && !error && market && !hosts.length ? (
        <p className="entity-panel__muted">
          No hosts tagged with store {storeId}.
        </p>
      ) : null}

      {/* Collapsed by default: a store runs to 30+ hosts, and expanding them
          all would bury the activity stream under the fold. The group header
          carries the count and the worst severity in it, so nothing is hidden
          that needs attention. */}
      {groups.map((group) => (
        <div className="store-hosts__group" key={group.type}>
          <button
            type="button"
            className="store-hosts__toggle"
            aria-expanded={open[group.type] ? "true" : "false"}
            onClick={() =>
              setOpen((prev) => ({ ...prev, [group.type]: !prev[group.type] }))
            }
          >
            <span
              className={`entity-dot entity-dot--${String(group.severity).toLowerCase()}`}
            />
            <span className="store-hosts__type">{group.type}</span>
            <span className="store-hosts__count">{group.hosts.length}</span>
            <span className="store-hosts__chevron" aria-hidden="true">
              {open[group.type] ? "▾" : "▸"}
            </span>
          </button>

          {open[group.type] ? (
            <ul className="store-hosts__list">
              {group.hosts.map((host) => (
                <li key={host.guid}>
                  <button
                    type="button"
                    className="entity-related__item"
                    onClick={() => onOpenEntity?.({ guid: host.guid })}
                    title={
                      host.reporting
                        ? host.name
                        : `${host.name} — not reporting`
                    }
                  >
                    <span
                      className={`entity-dot entity-dot--${String(host.severity).toLowerCase()}`}
                    />
                    <span className="entity-related__name">{host.name}</span>
                    {host.reporting ? null : (
                      <span className="entity-related__type">stale</span>
                    )}
                  </button>
                </li>
              ))}
            </ul>
          ) : null}
        </div>
      ))}
    </section>
  );
}
